import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { CardModel } from "../../../../swagger/api.client.generated";
import { CardShellComponent } from "../card-shell/card-shell.component";
import { CardCategoryIconComponent } from "../card-category-icon/card-category-icon.component";
import { CardContentComponent } from "../card-content/card-content.component";
import {
  BackgroundStyle,
  CardActionEvent,
  ControlOptions,
  UserAttribute,
} from "../card.models";
import { CardLocalService } from "../card.service";
import {
  applyCardEditResult,
  openEditCardDialog,
  processCardContent,
} from "../card-content.utils";
import { isBlankCardName, isMandatoryActivityCard } from "../card-type.utils";
import { AutoOpenCardQueueService } from "../auto-open-card-queue.service";
import { SnackbarService } from "../../snackbar-custom/snackbar.service";

@Component({
  selector: "app-activity-card",
  templateUrl: "./activity-card.component.html",
  styleUrls: ["./activity-card.component.scss"],
  standalone: true,
  imports: [CommonModule, CardShellComponent, CardCategoryIconComponent, CardContentComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityCardComponent implements OnChanges {
  @Input() cardModel: CardModel | null = null;
  /** Derived from CardVariant — true for mandatory/anchor cards (dark riverbed bg). */
  @Input() isMandatory: boolean = false;
  /** Derived from CardVariant — true for Deck 3 cards (editable). */
  @Input() isCustom: boolean = false;
  @Input() controls: ControlOptions = new ControlOptions();
  @Input() cardSelected: boolean = false;
  @Input() dragDisabled: boolean = false;
  /** ms before a press-and-hold becomes a drag; forwarded to the shell. Default 250. */
  @Input() dragStartDelay: number | { touch: number; mouse: number } = 250;
  @Input() set editMode(v: boolean) { this._editMode = v; }
  protected _editMode: boolean = false;

  @Output() cardAction = new EventEmitter<CardActionEvent>();
  @Output() userAttributes = new EventEmitter<UserAttribute>();

  backgroundStyle: string = "";
  borderCssClass: string = "";
  cardTypeCss: string = "";
  iconColorClass: string = "";
  isEditable: boolean = false;
  flip: "active" | "inactive" = "inactive";
  cardHtml: string = "";
  periodMinutes: number = 0;
  isMediaCard: boolean = false;
  mediaIcon: string = "fas fa-film";

  constructor(
    private dialog: MatDialog,
    private cardLocalService: CardLocalService,
    private cd: ChangeDetectorRef,
    private autoOpenQueue: AutoOpenCardQueueService,
    private snackbar: SnackbarService,
  ) {}

  /** A genuinely-custom card the user hasn't named yet. Gates the create-on-first-edit path
   *  (we only create a custom DB row for a real custom card, not any editable blank card). */
  get isUneditedCustom(): boolean {
    return this.isCustom && isBlankCardName(this.cardModel?.cardName);
  }

  /** Any card still on the blank sentinel name. Drives BOTH the on-card "Custom" placeholder
   *  label and the unsaved-custom styling, so the label and the custom look always appear
   *  together (a "Custom" card must look custom). */
  get isBlankPlaceholder(): boolean {
    return isBlankCardName(this.cardModel?.cardName);
  }

  ngOnChanges(): void {
    if (this.cardModel && this.isCustom && !this.isBlankPlaceholder) {
      this.cardLocalService.registerPlanCard(this.cardModel);
    }
    if (this.cardModel && (this.cardModel as any).autoOpenCreate) {
      (this.cardModel as any).autoOpenCreate = false;
      if (this.isUneditedCustom) {
        this.autoOpenQueue.enqueue(() =>
          this.openEditDialog("edit", {
            autoOpened: true,
            onClosed: () => this.autoOpenQueue.next(),
          })
        );
      }
    }
    if (this.isMandatory) {
      this.backgroundStyle = BackgroundStyle.Activity;
      this.borderCssClass = "ac-anchor";
      this.cardTypeCss = "activity";
      this.iconColorClass = "icon-wild-hare";
    } else if (this.isBlankPlaceholder) {
      this.backgroundStyle = BackgroundStyle.Custom;
      this.borderCssClass = "ac-activity";
      this.cardTypeCss = "activity_recommended";
      this.iconColorClass = "icon-lupine";
    } else {
      this.backgroundStyle = BackgroundStyle.ActivityRecommended;
      this.borderCssClass = "ac-activity";
      this.cardTypeCss = "activity_recommended";
      this.iconColorClass = "";
    }

    // All cards are now editable (previously gated to custom deck-3 cards). Mandatory cards are
    // still restricted to period-only edits, enforced in the edit dialog, not by hiding Edit.
    this.isEditable = true;
    const content = processCardContent(this.cardModel?.cardName ?? "");
    this.isMediaCard = content.isMediaCard;
    this.mediaIcon   = content.mediaIcon;
    this.cardHtml    = content.cardHtml;
    this.periodMinutes = this.cardModel?.periodMinutes ?? 0;
  }

  flipCard() {
    this.flip = this.flip === "inactive" ? "active" : "inactive";
    this.cardLocalService.cardFlipSound.play();
    this.cd.markForCheck();
  }

  onShellCardAction(event: CardActionEvent): void {
    if (event.action === "create" || event.action === "edit") {
      this.openEditDialog("edit");
    } else if (event.action === "duplicate") {
      this.openEditDialog("duplicate");
    } else {
      this.cardAction.emit(event);
    }
  }

  openEditDialog(
    mode: "edit" | "duplicate" = "edit",
    options?: { autoOpened?: boolean; onClosed?: () => void }
  ): void {
    if (!this.cardModel) {
      options?.onClosed?.();
      return;
    }

    const card = this.cardModel;
    // Brand-new custom placeholder = a create (not an edit): keep the CreateCustomCard path.
    const wasBrandNewCustom = this.isUneditedCustom;
    const isTracked = this.cardLocalService.hasLocalCustomCard(card.cardGuid);
    const isMandatory = isMandatoryActivityCard(card);

    openEditCardDialog(
      this.dialog,
      card,
      mode,
      this.cardLocalService.getDeckSnapshot(),
      isMandatory,
      { cardKind: "activity", isCreate: wasBrandNewCustom }
    ).subscribe({
      next: (result) => {
      if (!result || !this.cardModel) {
        // Cancelling a dialog we auto-opened (not one the user opened manually) drops the
        // just-added blank card from the plan, so nothing is left half-created.
        if (options?.autoOpened && this.cardModel) {
          this.cardAction.emit({ action: "remove", card: this.cardModel });
        }
        return;
      }

      if (wasBrandNewCustom) {
        // Outside an active plan edit, this.cardModel is the category's shared template
        // reference sitting in CardLocalService.cards[] (the picker reads it live). Editing it
        // in place would consume the template, leaving that category with no create affordance.
        // Clone instead, so the template stays in the cache untouched; a plan-in-progress slot
        // has no such shared reference to protect, so it's still filled in place as before.
        if (!this._editMode) {
          // Card Library context (deck-management dialog): there is no plan-save to create the DB
          // row, so persist immediately and refetch the deck. createLibraryCustomCard makes the card
          // server truth so it survives plan navigation (clearLocalCustomCards) and a page refresh —
          // upsertLocalCustomCard alone would be dropped on the next plan route.
          this.cardModel = Object.assign(new CardModel(), this.cardModel, { isPatientFavourite: false });
          this.applyLocalEdit(result);
          this.cardLocalService.createLibraryCustomCard(this.cardModel, 1).subscribe({
            next: (cardId) => {
              this.cardModel!.cardId = cardId;
              this.cardModel!.isEditableTemplate = false;
              this.cd.markForCheck();
              this.cardAction.emit({ action: "create", card: this.cardModel });
            },
            // On error the service surfaces a snackbar; don't fake success.
          });
          this.cd.markForCheck();
          return;
        }
        this.applyLocalEdit(result);
        // A newly created card is never favourited server-side, regardless of whether the shared
        // template it was cloned from happens to be.
        this.cardModel!.isPatientFavourite = false;
        // In-plan create: persist immediately and clear isEditableTemplate so the plan save does
        // not treat this as a pending template and create a duplicate card. Pass deckTypeId=1
        // explicitly: ActivityCardComponent always creates activity cards.
        this.cardLocalService.createCustomCardInDb(this.cardModel, 1).subscribe({
          next: (cardId) => {
            this.cardModel!.cardId = cardId;
            this.cardModel!.isEditableTemplate = false;
            this.cardLocalService.upsertLocalCustomCard(this.cardModel!);
            this.cd.markForCheck();
            this.cardAction.emit({ action: "create", card: this.cardModel });
          },
          error: () => {
            // The card has no DB row, so it keeps isEditableTemplate and serialises with
            // cardId: null. The API skips such cards but still creates the column they sit in,
            // leaving an activity-less column that renders as a blank gap (FLIP-54). Drop the
            // card rather than leave one in the plan that can never be saved — same outcome as
            // cancelling the auto-opened dialog above.
            this.snackbar.openDesignSnackbar("We encountered an issue", "Could not create card. Please try again.");
            this.cardAction.emit({ action: "remove", card: this.cardModel });
            this.cd.markForCheck();
          },
        });
        this.cd.markForCheck();
        return;
      }

      // Existing saved card: the backend decides in-place / new-version / copy-on-write / override.
      if (mode === "duplicate" && this._editMode) {
        // In plan edit mode: wait for the response so we can emit the new card (new cardId)
        // up to PlanCardActionService, which will add it to the plan.
        this.cardLocalService.editCard(this.cardModel, result).subscribe((response) => {
          if (response && !response.validationError && response.createdNewCard && response.cardId) {
            const duplicatedCard = Object.assign(new CardModel(), this.cardModel, {
              cardId: response.cardId,
              cardName: result.cardName,
              periodMinutes: result.periodMinutes ?? this.cardModel?.periodMinutes,
              cardDescription: result.description,
              cardSessionCardId: 0,
              // A duplicate is a genuinely new deck entry (matches the server, which always
              // creates it unfavourited) — it must not inherit the original's favourite status.
              isPatientFavourite: false,
            });
            this.cardAction.emit({ action: "duplicate", card: duplicatedCard });
          }
          this.cd.markForCheck();
        });
      } else {
        this.cardLocalService.editCard(this.cardModel, result).subscribe((response) => {
          // Reflect a direct edit on the in-plan instance immediately. Duplicate leaves the
          // original untouched (the copy surfaces via the deck refresh).
          if (mode === "edit" && this._editMode) {
            this.applyLocalEdit(result);
            // A DirectEdit that the backend resolved as copy-on-write creates a new real card —
            // repoint this slot at it (and force a fresh CardSessionCard on next save), otherwise
            // the plan keeps saving against the old, now-deactivated card.
            if (response && !response.validationError && response.createdNewCard && response.cardId) {
              this.cardModel!.cardId = response.cardId;
              this.cardModel!.cardSessionCardId = 0;
            }
            if (isTracked) this.cardLocalService.upsertLocalCustomCard(this.cardModel!);
          }

          this.cd.markForCheck();
          this.cardAction.emit({ action: mode, card: this.cardModel });
        });
      }
      },
      complete: () => options?.onClosed?.(),
    });
  }

  private applyLocalEdit(result: { cardName: string; periodMinutes?: number; description?: string }): void {
    if (!this.cardModel) return;
    this.cardHtml = applyCardEditResult(this.cardModel, result);
    this.periodMinutes = this.cardModel.periodMinutes;
    // cardModel is mutated in-place so ngOnChanges won't fire; restore derived display state manually.
    if (this.isMandatory) {
      this.backgroundStyle = BackgroundStyle.Activity;
      this.borderCssClass = "ac-anchor";
      this.cardTypeCss = "activity";
      this.iconColorClass = "icon-wild-hare";
    } else {
      this.backgroundStyle = BackgroundStyle.ActivityRecommended;
      this.iconColorClass = "";
    }
  }

}
